import { useEffect, useState } from "react";
import { CachedVouchers, VoucherSchema } from "../types/schemas";
import { getAvatarId, useJwt } from "../../../../../utils/jwt";

export function useSignedVoucher({ donationId }: { donationId: string }) {
    const [voucher, setVoucher] = useState<string | null>(null);
    const [error, setError] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(true);
    const jwt = useJwt();

    const getVoucher = async ({
        donationId,
        jwt,
    }: {
        donationId: string;
        jwt: string;
    }) => {
        const avatarId = getAvatarId(jwt ?? "") ?? "";

        const cachedVouchers = localStorage.getItem(
            `velvet-glitch-vouchers-${avatarId}`,
        );

        if (cachedVouchers) {
            try {
                const parsedCache = JSON.parse(cachedVouchers);
                const validationResult = CachedVouchers.safeParse(parsedCache);

                if (validationResult.success) {
                    const vouchers = validationResult.data;
                    const cachedVoucher = vouchers[donationId];

                    if (cachedVoucher) {
                        console.log(
                            "Using cached voucher for donationId:",
                            donationId,
                        );
                        setVoucher(cachedVoucher);
                        setLoading(false);
                        setError(false);
                        return;
                    } else {
                        console.log(
                            "No cached voucher found for donationId:",
                            donationId,
                        );
                    }
                } else {
                    console.error(
                        "Failed to validate cached vouchers:",
                        validationResult.error,
                    );

                    localStorage.removeItem(
                        `velvet-glitch-vouchers-${avatarId}`,
                    );
                }
            } catch (e) {
                console.error("Failed to parse cached vouchers:", e);
            }
        }

        const res = await fetch(
            `${window.CONVERSATIONS_API_BASE}/get-reward?donationId=${donationId}`,
            {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${jwt}`,
                },
            },
        );
        if (res.status !== 200) {
            setError(true);
            setLoading(false);
            throw new Error(`Failed to fetch final reward data: ${res.status}`);
        }
        const data = await res.json();

        const validationResult = VoucherSchema.safeParse(data);

        if (!validationResult.success) {
            setError(true);
            setLoading(false);
            console.error(
                "Failed to validate final reward data:",
                validationResult.error,
            );
            return;
        }

        const finalReward = validationResult.data;
        const token = finalReward.token ?? null;

        setError(false);
        setLoading(false);
        setVoucher(token);

        if (token !== null) {
            const updatedVouchers = cachedVouchers
                ? JSON.parse(cachedVouchers)
                : {};
            updatedVouchers[donationId] = token;
            localStorage.setItem(
                `velvet-glitch-vouchers-${avatarId}`,
                JSON.stringify(updatedVouchers),
            );
        }
    };

    useEffect(() => {
        if (!jwt) return;
        (async () => {
            try {
                await getVoucher({ donationId, jwt });
            } catch (e) {
                console.error("Error fetching final reward voucher:", e);
                setError(true);
            } finally {
                setLoading(false);
            }
        })();
    }, [donationId, jwt]);

    return { voucher, error, loading };
}
